import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type {
	AgentManagedExtensionAttributes,
	AgentManagedExtensionParameters,
	AgentManagedObjectAttribute,
} from '../../common/agentManagedExtensionPluginType';
import {
	findAgentManagedExtension,
	findAgentManagedExtensionByLocalId,
	getNodeAiManagedObject,
} from '../../common/utils';
import { setMeta, type AgentManagedExtensionMeta } from '../pm-plugins/state';

export type AgentManagedExtensionNodeWithPos = {
	node: PMNode;
	pos: number;
};

export const getAiManagedObjectParameter = ({
	config,
	id,
}: AiManagedObject): AgentManagedObjectAttribute => ({
	configVersion: String(config.version ?? ''),
	id,
});

export const dispatchAgentManagedExtensionMeta = (
	editorView: EditorView,
	meta: AgentManagedExtensionMeta,
): void => {
	editorView.dispatch(setAgentManagedExtensionMeta(editorView.state.tr, meta));
};

export const setAgentManagedExtensionMeta = (
	tr: Transaction,
	meta: AgentManagedExtensionMeta,
): Transaction => {
	setMeta(tr, meta);
	tr.setMeta('addToHistory', false);
	return tr;
};

export const findConfiguredAgentManagedExtension = (
	editorView: EditorView,
	configLocalId?: string,
): AgentManagedExtensionNodeWithPos | undefined =>
	(configLocalId
		? findAgentManagedExtensionByLocalId(editorView.state, configLocalId)
		: undefined) ?? findAgentManagedExtension(editorView.state);

export const updateNodeAiManagedObject = (
	editorView: EditorView,
	aimo: AiManagedObject,
	localId?: string,
): void => {
	const tr = editorView.state.tr;
	if (setNodeAiManagedObject(editorView.state, tr, aimo, localId)) {
		editorView.dispatch(tr);
	}
};

export const setNodeAiManagedObject = (
	editorState: EditorState,
	tr: Transaction,
	aimo: AiManagedObject,
	localId?: string,
): boolean => {
	const currentFound =
		findAgentManagedExtensionByLocalId(editorState, localId) ?? findAgentManagedExtension(editorState);
	if (!currentFound) {
		return false;
	}

	const { node, pos } = currentFound;
	const existingAttrs = node.attrs as AgentManagedExtensionAttributes;
	const existingAiManagedObject = getNodeAiManagedObject(existingAttrs);
	const aiManagedObject = getAiManagedObjectParameter(aimo);
	if (
		existingAiManagedObject?.id === aiManagedObject.id &&
		existingAiManagedObject.configVersion === aiManagedObject.configVersion
	) {
		return false;
	}

	const parameters = (existingAttrs.parameters ?? {}) as AgentManagedExtensionParameters;
	tr.setNodeMarkup(pos, undefined, {
		...node.attrs,
		parameters: {
			...parameters,
			macroParams: {
				...parameters.macroParams,
				aiManagedObject: {
					// Confluence macro params persist scalar values reliably. Keep
					// the public node metadata as an object by parsing on read.
					value: JSON.stringify(aiManagedObject),
				},
			},
		},
	});
	tr.setMeta('addToHistory', false);
	return true;
};
